import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Only anchored panels need repositioning. In the vertical filter sidebar and the mobile filters
    // drawer the panel sits in normal flow, where `top`/`left` do nothing — but writing them still
    // mutates the style attribute, which feeds straight back into the MutationObserver below and
    // pins the main thread for as long as the panel is open. Bail out before touching any style.
    if (getComputedStyle(this).position === 'static') return;

    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Assigning an unchanged value still rewrites the style attribute, so guard every write.
    this.#setStyle('top', OFFSET + 'px');

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.#setStyle('left', `-${overflowAmount}px`);
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.#setStyle('left', `${overflowAmount}px`);
    }

    this.#mutationObserver.takeRecords();
  };

  /**
   * Writes an inline style only when it actually changes.
   *
   * @param {'top' | 'left'} property - The property to set.
   * @param {string} value - The value to set it to.
   */
  #setStyle(property, value) {
    if (this.style[property] === value) return;

    this.style[property] = value;
  }

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
